import { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { toast } from "sonner";
import Shell from "@/layout/Shell";
import { useAuth, isAdmin } from "@/lib/auth";
import KpiGrid from "@/components/KpiGrid";
import Queue from "@/components/Queue";
import Kanban from "@/components/Kanban";
import NewTicketDialog from "@/components/NewTicketDialog";
import WhatsAppConfig from "@/components/WhatsAppConfig";
import ReportsView from "@/pages/Reports";
import SubCategoryDialog from "@/components/SubCategoryDialog";
import Knowledge from "@/pages/admin/Knowledge";
import BulkImportUsers from "@/pages/admin/BulkImportUsers";
import Help from "@/pages/Help";
import { StatusDonut, CategoryDonut, AssigneeLoadChart, SlaRing } from "@/components/Charts";
import { useFetch } from "@/lib/useFetch";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  AlertTriangle, Plus, KeyRound, Ban, CheckCircle2, Mail, Copy, Pencil, ListTree, Trash2, ChevronsUpDown, Check } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { colorFor, initialsOf, hoursAgo } from "@/lib/mock";

const ICONS = ["mail","key","monitor","network","wifi","video","printer","copy","camera","globe","tool","users","building","shield","clock","chart","inbox","ticket"];
const ROLE_LABELS = { requester: "Institute user", technician: "Technician (sub-category handler)", authority: "Category head", admin: "Central admin", superadmin: "Superadmin" };

function useAdminData() {
  const dash = useFetch(() => api.dashboard().then((r) => r?.stats || {}), []);
  const tickets = useFetch(() => api.tickets({ limit: 200 }).then((r) => r?.tickets || []), []);
  const cats = useFetch(() => api.categories({ all: 1 }).then((r) => r?.categories || []), []);
  const team = useFetch(() => api.users({ role: "team", limit: 500 }).then((r) => r?.users || []), []);
  const reloadAll = () => { dash.reload(); tickets.reload(); };
  return { dash, tickets, cats, team, reloadAll };
}

/* ============================================================ */
function Overview() {
  const { dash, tickets, cats, team, reloadAll } = useAdminData();
  const stats = dash.data || {};
  const list = tickets.data || [];
  const breach = list.filter((t) => (t.sla_pct ?? 0) >= 80 && !["resolved","closed"].includes(t.status)).length;

  const openByAssignee = stats.byAssignee || {};
  const openByCategory = stats.byCategory || {};

  return (
    <Shell role="admin" title="Admin Overview" subtitle="Pulse of your service desk today." actions={<NewTicketDialog categories={cats.data || []} onCreated={reloadAll} />}>
      {breach > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl border border-rose-200 dark:border-rose-900/40 bg-rose-500/5 p-4 flex items-center gap-3"
          data-testid="sla-banner"
        >
          <div className="h-9 w-9 rounded-xl bg-rose-500/15 grid place-items-center text-rose-600">
            <AlertTriangle className="h-4 w-4" />
          </div>
          <div className="flex-1">
            <div className="font-medium">{breach} tickets approaching SLA breach</div>
            <div className="text-xs text-muted-foreground">Review before auto-escalation triggers.</div>
          </div>
        </motion.div>
      )}

      <KpiGrid
        items={[
          { label: "Open tickets",  value: (stats.open || 0) + (stats.assigned || 0) + (stats.in_progress || 0), accent: "#0047FF", note: `${stats.today || 0} today` },
          { label: "In progress",   value: stats.in_progress || 0, accent: "#F59E0B", note: "actively worked on" },
          { label: "Resolved",      value: stats.resolved || 0,    accent: "#10B981", note: `${stats.avgResolveHrs ? `avg ${stats.avgResolveHrs.toFixed?.(1) ?? stats.avgResolveHrs}h` : "—"}` },
          { label: "Overdue",       value: stats.overdue || 0,     accent: "#EF4444", note: "SLA at risk" },
        ]}
      />

      <div className="grid xl:grid-cols-[1.5fr_1fr] gap-6">
        <StatusDonut stats={stats} />
        <SlaRing overdue={stats.overdue || 0} total={stats.total || 0} />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <CategoryDonut byCategory={openByCategory} categories={cats.data || []} />
        <AssigneeLoadChart byAssignee={openByAssignee} users={team.data || []} />
      </div>

      <div>
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Team</div>
            <h3 className="font-display text-2xl font-extrabold tracking-tighter">Technicians</h3>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="tech-grid">
          {(team.data || []).slice(0, 10).map((t, i) => {
            const load = openByAssignee[t.id] || 0;
            return (
              <motion.div
                key={t.id}
                initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.04 }}
                className="glass rounded-2xl p-4"
                data-testid={`tech-card-${t.id}`}
              >
                <div className="flex items-center gap-2.5">
                  <Avatar className="h-10 w-10">
                    <AvatarFallback className="font-display font-bold" style={{ background: colorFor(t.id), color: "white" }}>
                      {initialsOf(t.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{t.name}</div>
                    <div className="text-[11px] text-muted-foreground truncate capitalize">{t.role}</div>
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="text-muted-foreground">Open</span>
                  <span className="text-mono">{load}</span>
                </div>
                <Progress value={Math.min(100, load * 10)} className="h-1.5 mt-1" />
              </motion.div>
            );
          })}
          {team.data?.length === 0 && <div className="text-sm text-muted-foreground col-span-full">No technicians yet.</div>}
        </div>
      </div>
    </Shell>
  );
}

function KanbanPage() {
  const { tickets, cats, reloadAll } = useAdminData();
  return (
    <Shell role="admin" title="Kanban Board" subtitle="Drag tickets across stages." actions={<NewTicketDialog categories={cats.data || []} onCreated={reloadAll} />}>
      <Kanban tickets={tickets.data || []} onChanged={reloadAll} />
    </Shell>
  );
}

function MyRequestsPage() {
  const cats = useFetch(() => api.categories().then((r) => r?.categories || []), []);
  const mine = useFetch(() => api.tickets({ limit: 200, scope: "mine" }).then((r) => r?.tickets || []), []);
  return (
    <Shell role="admin" title="My Requests" subtitle="Tickets you raised yourself — separate from your queue."
      actions={<NewTicketDialog categories={cats.data || []} onCreated={mine.reload} />}>
      {(mine.data || []).length === 0 ? (
        <div className="glass rounded-2xl p-8 text-center">
          <div className="font-display text-xl font-extrabold tracking-tighter">No requests yet</div>
          <p className="text-sm text-muted-foreground mt-1">
            Anything you raise yourself appears here. Work assigned to you or to your categories stays in <b>Ticket Queue</b>.
          </p>
        </div>
      ) : (
        <Queue tickets={mine.data || []} categories={cats.data || []} onChanged={mine.reload} requesterOnly />
      )}
    </Shell>
  );
}

function QueuePage() {
  const { user } = useAuth();
  const { cats, reloadAll } = useAdminData();
  // A category head's queue is the work they are responsible for. Their own requests
  // live under "My Requests" so the two are never mixed up.
  const scoped = useFetch(() => api.tickets({
    limit: 200, ...(user?.role === "authority" ? { scope: "queue" } : {}),
  }).then((r) => r?.tickets || []), [user?.role]);
  const tickets = user?.role === "authority" ? scoped : null;
  return (
    <Shell role="admin"
      title={user?.role === "authority" ? "Category Queue" : "Ticket Queue"}
      subtitle={user?.role === "authority" ? "Tickets in the categories you are incharge of." : "Filter, search, and triage."}
      actions={<NewTicketDialog categories={cats.data || []} onCreated={reloadAll} />}>
      <QueueBody tickets={tickets} cats={cats} reloadAll={reloadAll} />
    </Shell>
  );
}

function QueueBody({ tickets, cats, reloadAll }) {
  const { tickets: allTickets } = useAdminData();
  const src = tickets || allTickets;
  return <Queue tickets={src.data || []} categories={cats.data || []} onChanged={() => { src.reload?.(); reloadAll?.(); }} />;
}

/* ============================================================
 * Team & Requesters — pending queue, add member, manage
 * ============================================================ */
function AddUserDialog({ role, onAdded }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({ name: "", email: "", password: "", phone: "", department: "", role: role || "technician" });
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));
  const needsDept = f.role === "hod";

  const submit = async (e) => {
    e.preventDefault();
    if (f.password.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    if (needsDept && !f.department.trim()) { toast.error("Department is required for an HOD account."); return; }
    setBusy(true);
    try {
      await api.createUser(f);
      toast.success(`${ROLE_LABELS[f.role] || "Team member"} added`);
      setOpen(false);
      onAdded?.();
    } catch (err) { toast.error(err.message || "Failed to add"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5"><Plus className="h-4 w-4" /> Add team member</Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Add team member</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Role</Label>
            <Select value={f.role} onValueChange={(v) => set("role")(v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="technician">Technician — handles sub-category tickets</SelectItem>
                <SelectItem value="authority">Category head — owns a category &amp; its escalation</SelectItem>
                <SelectItem value="admin">Central admin — runs the whole institute</SelectItem>
                <SelectItem value="requester">Institute user — raises tickets only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div><Label>Name</Label><Input value={f.name} onChange={set("name")} className="mt-1" required /></div>
          <div><Label>Email</Label><Input value={f.email} onChange={set("email")} className="mt-1" required /></div>
          <div><Label>Password</Label><Input type="password" value={f.password} onChange={set("password")} className="mt-1" minLength={8} required /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Phone</Label><Input value={f.phone} onChange={set("phone")} className="mt-1" /></div>
            <div>
              <Label>Department{needsDept ? " *" : ""}</Label>
              <Input value={f.department} onChange={set("department")} className="mt-1" required={needsDept}
                placeholder={needsDept ? "e.g. Computer Science" : ""} />
              {needsDept && <p className="text-[11px] text-muted-foreground mt-1">Determines which department's tickets this HOD receives.</p>}
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">{busy ? "Adding…" : "Add"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function UserRow({ u, onChanged, showDept }) {
  const act = async (patch, msg) => {
    try { await api.updateUser(u.id, patch); toast.success(msg); onChanged?.(); }
    catch (e) { toast.error(e.message || "Failed"); }
  };
  return (
    <TableRow>
      <TableCell>
        <div className="flex items-center gap-2">
          <Avatar className="h-7 w-7"><AvatarFallback style={{ background: colorFor(u.id), color: "white" }} className="text-[10px] font-bold">{initialsOf(u.name)}</AvatarFallback></Avatar>
          <div><div className="font-medium">{u.name}</div><div className="text-[11px] text-muted-foreground">{u.email}{u.phone ? ` · ${u.phone}` : ""}</div></div>
        </div>
      </TableCell>
      {showDept && <TableCell className="text-xs text-muted-foreground">{u.department || "—"}</TableCell>}
      <TableCell>
        {u.role === "superadmin" ? (
          <span className="text-[11px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/5">Superadmin</span>
        ) : (
          <Select
            value={u.role}
            onValueChange={(v) => {
              if (v === u.role) return;
              if (!window.confirm(`Change ${u.name} to "${ROLE_LABELS[v]}"?\n\nThey will be signed out and must log in again for the new role to apply.`)) return;
              act({ role: v }, `${u.name} is now ${ROLE_LABELS[v]} — they must sign in again`);
            }}
          >
            <SelectTrigger className="h-7 w-[210px] text-[11px]" data-testid={`role-${u.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="requester">Institute user</SelectItem>
              <SelectItem value="technician">Technician (sub-category handler)</SelectItem>
              <SelectItem value="authority">Category head</SelectItem>
              <SelectItem value="admin">Central admin</SelectItem>
            </SelectContent>
          </Select>
        )}
      </TableCell>
      <TableCell>
        {u.approval_status === "pending" ? (
          <div className="flex gap-1.5">
            <Button size="sm" className="h-7 bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => act({ approval_status: "approved" }, "Approved")}>Approve</Button>
            <Button size="sm" variant="outline" className="h-7" onClick={() => act({ approval_status: "rejected" }, "Rejected")}>Reject</Button>
          </div>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs">
            <span className={`h-2 w-2 rounded-full ${u.is_active ? "bg-emerald-500" : "bg-zinc-400"}`} /> {u.is_active ? "active" : "disabled"}
          </span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="inline-flex gap-1.5">
          <Button size="sm" variant="outline" className="h-8 gap-1" onClick={() => {
            const pw = prompt(`New password for ${u.name}`); if (pw && pw.length >= 8) act({ new_password: pw }, "Password reset");
            else if (pw) toast.error("Password must be 8+ characters");
          }}><KeyRound className="h-3.5 w-3.5" /></Button>
          <Button size="sm" variant={u.is_active ? "outline" : "default"} className="h-8 gap-1"
            onClick={() => act({ is_active: !u.is_active }, u.is_active ? "Disabled" : "Enabled")}>
            {u.is_active ? <Ban className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
          </Button>
        </div>
      </TableCell>
    </TableRow>
  );
}

function TeamPage() {
  const team = useFetch(() => api.users({ role: "team", limit: 500 }).then((r) => r?.users || []), []);
  const requesters = useFetch(() => api.users({ role: "requester", limit: 500 }).then((r) => r?.users || []), []);
  const pending = (requesters.data || []).filter((u) => u.approval_status === "pending");
  const approved = (requesters.data || []).filter((u) => u.approval_status !== "pending");

  return (
    <Shell role="admin" title="Users & Team" subtitle="My team, requesters, and pending approvals." actions={<AddUserDialog onAdded={team.reload} />}>
      {pending.length > 0 && (
        <div className="rounded-2xl border border-amber-200 dark:border-amber-900/40 bg-amber-500/5 p-4" data-testid="pending-banner">
          <div className="font-medium mb-2">{pending.length} registration{pending.length > 1 ? "s" : ""} awaiting approval</div>
          <Table>
            <TableBody>
              {pending.map((u) => <UserRow key={u.id} u={u} onChanged={requesters.reload} />)}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="glass rounded-2xl p-5">
        <div className="overline mb-2">My team</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Department</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Role</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(team.data || []).map((u) => <UserRow key={u.id} u={u} onChanged={team.reload} showDept />)}
            {team.data?.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-sm text-muted-foreground">No team members yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <div className="glass rounded-2xl p-5">
        <div className="overline mb-2">Requesters</div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Name</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Role</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest">Status</TableHead>
              <TableHead className="text-mono text-[10px] uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {approved.map((u) => <UserRow key={u.id} u={u} onChanged={requesters.reload} />)}
            {approved.length === 0 && <TableRow><TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">No requesters yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </Shell>
  );
}

/* ============================================================
 * Categories / service catalogue — full CRUD + routing
 * ============================================================ */
const LEVEL_LABEL = { 1: "L1 — First responder", 2: "L2 — Escalation", 3: "L3 — Higher escalation" };

/* Searchable combobox for picking a team member — reused in Category, Escalation, and Incharges forms. */
function TeamPicker({ team, value, onValueChange, placeholder = "Auto-route (least busy)", className = "" }) {
  const [open, setOpen] = useState(false);
  const selected = (team || []).find((t) => t.id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open}
          className={`w-full justify-between font-normal text-sm h-9 ${className}`}>
          {selected
            ? <span className="truncate">{selected.name}</span>
            : <span className="text-muted-foreground truncate">{placeholder}</span>}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-64" align="start">
        <Command>
          <CommandInput placeholder="Search by name…" className="h-9" />
          <CommandList>
            <CommandEmpty>No match found.</CommandEmpty>
            <CommandItem value="__auto__" onSelect={() => { onValueChange(""); setOpen(false); }}>
              <Check className={`mr-2 h-4 w-4 ${!value ? "opacity-100" : "opacity-0"}`} />
              {placeholder}
            </CommandItem>
            {(team || []).map((t) => (
              <CommandItem key={t.id} value={t.name} onSelect={() => { onValueChange(t.id); setOpen(false); }}>
                <Check className={`mr-2 h-4 w-4 ${value === t.id ? "opacity-100" : "opacity-0"}`} />
                {t.name}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function EscalationRow({ lv, allUsers, onChange, onRemove }) {
  const set = (k) => (e) => onChange({ ...lv, [k]: e?.target ? e.target.value : e });

  // When a user is picked from the dropdown, auto-fill email, phone and name label
  // from the user record. Manual fields remain editable after auto-fill.
  const handlePersonSelect = (userId) => {
    if (!userId) {
      onChange({ ...lv, notify_id: "", notify_email: "", notify_phone: "", label: "" });
      return;
    }
    const user = (allUsers || []).find((u) => u.id === userId);
    onChange({
      ...lv,
      notify_id:    userId,
      notify_email: user?.email || lv.notify_email || "",
      notify_phone: user?.phone || lv.notify_phone || "",
      label:        user?.name  || lv.label        || "",
    });
  };

  return (
    <div className="rounded-xl border p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold">{LEVEL_LABEL[lv.level] || `L${lv.level}`}</span>
        <button type="button" onClick={onRemove} className="text-xs text-red-600 hover:underline">Remove</button>
      </div>
      <div>
        <Label className="text-xs">Person (optional — any institute user)</Label>
        <TeamPicker
          team={allUsers}
          value={lv.notify_id || ""}
          onValueChange={handlePersonSelect}
          placeholder="Not a system user"
          className="mt-1"
        />
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Email {lv.notify_id ? "(optional)" : "(required)"}</Label>
          <Input type="email" value={lv.notify_email || ""} onChange={set("notify_email")}
            placeholder="coe@institute.edu" className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Contact number</Label>
          <Input value={lv.notify_phone || ""} onChange={set("notify_phone")}
            placeholder="+91…" className="mt-1 h-9" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs">Label (office name)</Label>
          <Input value={lv.label || ""} onChange={set("label")} placeholder="COE Office" className="mt-1 h-9" />
        </div>
        <div>
          <Label className="text-xs">Escalate after (hours)</Label>
          <Input type="number" min={1} value={lv.after_hours} onChange={set("after_hours")} className="mt-1 h-9" />
        </div>
      </div>
    </div>
  );
}

function CategoryDialog({ cat, team, allUsers, incharges, onSaved, trigger }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [levels, setLevels] = useState([]);
  const [f, setF] = useState(() => ({
    name: cat?.name || "", icon: cat?.icon || "tool", description: cat?.description || "",
    sla_hours: cat?.sla_hours || 24, default_assignee: cat?.default_assignee || cat?.assignee?.id || "",
    is_active: cat?.is_active !== false,
    authority_id: cat?.authority_id ? String(cat.authority_id) : "",
    is_confidential: !!cat?.is_confidential,
    allow_anonymous: !!cat?.allow_anonymous,
    audience: cat?.audience === "public" ? "public" : "internal",
  }));
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  // load the existing escalation chain when editing
  useEffect(() => {
    if (!open || !cat?.id) return;
    api.escalation(cat.id)
      .then((r) => setLevels((r.levels || []).map((l) => ({
        level: l.level, notify_id: l.notify_id || "", notify_email: l.notify_email || "",
        notify_phone: l.notify_phone || "", label: l.label || "", after_hours: l.after_hours || 24,
      }))))
      .catch(() => {});
  }, [open, cat?.id]);

  const addLevel = () => {
    const next = (levels.length ? Math.max(...levels.map((l) => l.level)) : 0) + 1;
    setLevels((s) => [...s, { level: next, notify_id: "", notify_email: "", notify_phone: "", label: "", after_hours: next * 24 }]);
  };

  const submit = async (e) => {
    e.preventDefault();
    for (const l of levels) {
      if (!l.notify_id && !(l.notify_email || "").trim())
        return toast.error(`Level ${l.level} needs a person or an email address.`);
    }
    setBusy(true);
    try {
      const payload = {
        ...f,
        sla_hours: parseInt(f.sla_hours) || 24,
        default_assignee: f.default_assignee || null,
        authority_id: f.authority_id ? Number(f.authority_id) : null,
      };
      let catId = cat?.id;
      if (cat) await api.updateCategory(cat.id, payload);
      else {
        const res = await api.createCategory(payload);
        catId = res?.category?.id;
        if (!catId) {   // fallback for older backends that don't return the row
          const fresh = await api.categories({ all: 1 });
          catId = (fresh.categories || []).find((c) => c.name === payload.name)?.id;
        }
      }
      if (levels.length && !catId) {
        toast.error("Service saved, but the escalation chain could not be attached. Edit the service to set it.");
      }
      if (catId && levels.length) await api.setEscalation(catId, levels);
      toast.success(cat ? "Service updated" : "Service created");
      setOpen(false);
      onSaved?.();
    } catch (err) { toast.error(err.message || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{cat ? "Edit service type" : "New service type"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Name</Label><Input value={f.name} onChange={set("name")} className="mt-1" required /></div>
          <div>
            <Label>Icon</Label>
            <Select value={f.icon} onValueChange={(v) => set("icon")(v)}>
              <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>{ICONS.map((i) => <SelectItem key={i} value={i} className="capitalize">{i}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea value={f.description} onChange={set("description")} className="mt-1" rows={2} /></div>

          <div>
            <Label>Incharge for this category</Label>
            <Select value={f.authority_id || "none"} onValueChange={(v) => set("authority_id")(v === "none" ? "" : v)}>
              <SelectTrigger className="mt-1"><SelectValue placeholder="No incharge" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No incharge (central handling)</SelectItem>
                {(incharges || []).map((a) => (
                  <SelectItem key={a.id} value={String(a.id)}>
                    {a.name}{a.department ? ` (${a.department})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground mt-1">
              New tickets here are routed to this incharge. Manage incharges on the Categories page.
            </p>
          </div>

          <label className="flex items-start gap-2 text-sm rounded-lg bg-amber-50 dark:bg-amber-500/5 border border-amber-200 p-2.5">
            <Switch checked={f.is_confidential} onCheckedChange={(v) => { set("is_confidential")(v); if (v) set("audience")("internal"); }} />
            <span>
              <b>Confidential</b> — e.g. harassment or safety reports.
              <span className="block text-[11px] text-muted-foreground mt-0.5">
                Visible only to this category&apos;s incharge and the superadmin. Hidden from other admins,
                technicians, dashboards and reports — including you, unless you are the incharge.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-2 text-sm rounded-lg bg-black/5 dark:bg-white/5 p-2.5">
            <Switch checked={f.allow_anonymous} onCheckedChange={(v) => set("allow_anonymous")(v)} />
            <span>
              <b>Allow anonymous reporting</b>
              <span className="block text-[11px] text-muted-foreground mt-0.5">
                The reporter can file without revealing their identity.
              </span>
            </span>
          </label>

          <label className={`flex items-start gap-2 text-sm rounded-lg p-2.5 border ${f.is_confidential ? "opacity-50 pointer-events-none" : ""} bg-black/5 dark:bg-white/5`}>
            <Switch checked={f.audience === "public"}
              onCheckedChange={(v) => set("audience")(v ? "public" : "internal")} />
            <span>
              <b>Open to external users</b>
              <span className="block text-[11px] text-muted-foreground mt-0.5">
                People who are not institution members — e.g. someone who booked a venue through
                the events app — can file here. They see <b>only</b> categories marked this way,
                and only their own tickets. Everything else stays invisible to them.
                {f.is_confidential && " A confidential category can never be opened up."}
              </span>
            </span>
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div><Label>SLA (hours)</Label><Input type="number" min={1} value={f.sla_hours} onChange={set("sla_hours")} className="mt-1" /></div>
            <div>
              <Label>Default assignee</Label>
              <TeamPicker
                team={(team || []).map((t) => ({ ...t, name: `${t.name} (${ROLE_LABELS[t.role] || t.role})` }))}
                value={f.default_assignee || ""}
                onValueChange={(v) => set("default_assignee")(v)}
                placeholder="Auto-route"
                className="mt-1"
              />
            </div>
          </div>

          {/* Escalation chain */}
          <div className="rounded-xl border p-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold">Escalation chain</div>
                <div className="text-[11px] text-muted-foreground">Who to notify if this ticket stays unresolved.</div>
              </div>
              <Button type="button" size="sm" variant="outline" onClick={addLevel} className="rounded-full h-8">
                + Add level
              </Button>
            </div>
            {levels.length === 0 && (
              <p className="text-xs text-muted-foreground">No escalation configured — tickets stay with the assignee.</p>
            )}
            {[...levels].sort((a, b) => a.level - b.level).map((lv) => (
              <EscalationRow key={lv.level} lv={lv} allUsers={allUsers}
                onChange={(n) => setLevels((s) => s.map((x) => (x.level === lv.level ? n : x)))}
                onRemove={() => setLevels((s) => s.filter((x) => x.level !== lv.level).map((x, idx) => ({ ...x, level: idx + 1 })))}
              />
            ))}
          </div>

          {cat && (
            <label className="flex items-center gap-2 text-sm"><Switch checked={f.is_active} onCheckedChange={(v) => set("is_active")(v)} /> Active</label>
          )}
          <DialogFooter>
            <Button type="submit" disabled={busy} className="w-full rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white">{busy ? "Saving…" : cat ? "Save changes" : "Create service"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function InchargesPanel({ team, allUsers, incharges, onChanged }) {
  const [f, setF] = useState({ type: "coe", name: "", department: "", holder_id: "", contact_email: "", contact_phone: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e?.target ? e.target.value : e }));

  const create = async () => {
    if (!f.name.trim()) return toast.error("Name is required");
    if (f.type === "hod" && !f.department.trim()) return toast.error("HOD needs a department");
    setBusy(true);
    try {
      const res = await api.createAuthority({
        type: f.type, name: f.name.trim(),
        department: f.type === "hod" ? f.department.trim() : undefined,
        holder_id: f.holder_id || undefined,
        contact_email: f.contact_email.trim() || undefined,
        contact_phone: f.contact_phone.trim() || undefined,
      });
      const promo = res?.role_change;
      if (promo?.promoted) toast.success(`Incharge added — ${promo.name} is now a Category head (they must sign in again)`);
      else toast.success("Incharge added");
      setF({ type: "coe", name: "", department: "", holder_id: "", contact_email: "", contact_phone: "" });
      onChanged?.();
    } catch (e) { toast.error(e.message || "Could not add"); }
    finally { setBusy(false); }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove "${a.name}"? Its categories will fall back to central handling.`)) return;
    try { await api.deleteAuthority(a.id); toast.success("Removed"); onChanged?.(); }
    catch (e) { toast.error(e.message); }
  };

  return (
    <div className="glass rounded-2xl p-5 mb-6" data-testid="incharges-panel">
      <div className="overline mb-1">Category incharges</div>
      <p className="text-xs text-muted-foreground mb-3">
        The people who own a kind of ticket — COE for exams, Dean for student welfare, an HOD per department.
      </p>

      <div className="space-y-2 mb-4">
        {(incharges || []).length === 0 && <div className="text-sm text-muted-foreground">No incharges yet.</div>}
        {(incharges || []).map((a) => (
          <div key={a.id} className="flex items-center gap-3 rounded-xl border p-3">
            <div className="h-9 w-9 rounded-lg bg-cobalt-500/10 text-cobalt-500 grid place-items-center text-[10px] font-mono uppercase shrink-0">
              {a.type === "hod" ? (a.department || "HOD") : a.type.slice(0, 3)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{a.name}{a.department ? ` · ${a.department}` : ""}</div>
              <div className="text-xs text-muted-foreground truncate">
                {a.holder?.name ? a.holder.name : (a.contact_email || "No contact set")}
                {a.contact_phone ? ` · ${a.contact_phone}` : ""}
              </div>
            </div>
            {a.type !== "central" && (
              <Button variant="outline" size="sm" onClick={() => remove(a)}
                className="rounded-full text-red-600 border-red-200 hover:bg-red-50">Remove</Button>
            )}
          </div>
        ))}
      </div>

      <div className="rounded-xl border p-3 space-y-3">
        <div className="text-sm font-semibold">Add an incharge</div>
        <div className="grid md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs">Type</Label>
            <Select value={f.type} onValueChange={(v) => set("type")(v)}>
              <SelectTrigger className="mt-1 h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="coe">Controller of Examinations</SelectItem>
                <SelectItem value="dean">Dean (e.g. Student Welfare)</SelectItem>
                <SelectItem value="hod">Head of Department</SelectItem>
                <SelectItem value="custom">Other office</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Name / office</Label>
            <Input value={f.name} onChange={set("name")} placeholder="Dean, Student Welfare" className="mt-1 h-9" />
          </div>
          {f.type === "hod" && (
            <div>
              <Label className="text-xs">Department</Label>
              <Input value={f.department} onChange={set("department")} placeholder="CSE" className="mt-1 h-9 uppercase" />
            </div>
          )}
          <div>
            <Label className="text-xs">Person (optional — any institute user)</Label>
            <TeamPicker
              team={allUsers || team}
              value={f.holder_id || ""}
              onValueChange={(v) => set("holder_id")(v)}
              placeholder="Not a system user"
              className="mt-1"
            />
          </div>
          <div>
            <Label className="text-xs">Contact email</Label>
            <Input type="email" value={f.contact_email} onChange={set("contact_email")} placeholder="coe@institute.edu" className="mt-1 h-9" />
          </div>
          <div>
            <Label className="text-xs">Contact number</Label>
            <Input value={f.contact_phone} onChange={set("contact_phone")} placeholder="+91…" className="mt-1 h-9" />
          </div>
        </div>
        <Button type="button" onClick={create} disabled={busy}
          className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5 h-9">
          <Plus className="h-4 w-4" /> {busy ? "Adding…" : "Add incharge"}
        </Button>
      </div>
    </div>
  );
}

function CategoryDeleteButton({ cat, onDeleted }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!isAdmin(user)) return null;          // only admins may delete a service
  const del = async () => {
    if (!window.confirm(`Delete the service "${cat.name}"?\n\nThis also removes its sub-categories and escalation. If it already has tickets, deletion is blocked — deactivate it instead.`)) return;
    setBusy(true);
    try {
      await api.deleteCategory(cat.id);
      toast.success(`"${cat.name}" deleted`);
      onDeleted?.();
    } catch (e) { toast.error(e.message || "Could not delete"); }
    finally { setBusy(false); }
  };
  return (
    <Button size="icon" variant="ghost" disabled={busy} onClick={del}
      className="h-7 w-7 text-red-600 hover:bg-red-50" title="Delete service">
      <Trash2 className="h-3.5 w-3.5" />
    </Button>
  );
}

function CategoriesPage() {
  const { user } = useAuth();
  const canAdmin = isAdmin(user);                       // central admin / superadmin
  const cats = useFetch(() => api.categories({ all: 1 }).then((r) => r?.categories || []), []);
  const team = useFetch(() => api.users({ role: "team", limit: 500 }).then((r) => r?.users || []), []);
  // staff users: all institute staff regardless of role (excludes students).
  // Used for escalation person pickers and incharge "holder" so HODs, Deans,
  // COE, faculty etc. appear even if their role is 'requester'.
  const allUsers = useFetch(() => api.users({ user_type: 'staff', limit: 1000 }).then((r) => r?.users || []), []);
  const incharges = useFetch(() => api.authorities().then((r) => r?.authorities || []), []);

  // Which incharge roles does THIS user hold? A category head should only ever manage
  // the categories owned by those incharges — not the whole institute's list.
  // An incharge belongs to me if I'm the linked Person OR its contact email is mine.
  const isMine = (a) =>
    a.holder_id === user?.id ||
    (a.contact_email || "").toLowerCase() === (user?.email || "").toLowerCase();
  const myAuthIds = (incharges.data || []).filter(isMine).map((a) => a.id);
  const myAuthNames = (incharges.data || []).filter(isMine)
    .map((a) => a.name + (a.department ? ` (${a.department})` : ""));
  const visible = canAdmin
    ? (cats.data || [])
    : (cats.data || []).filter((c) => myAuthIds.includes(c.authority_id));

  return (
    <Shell role="admin"
      title={canAdmin ? "Categories & Services" : "My categories"}
      subtitle={canAdmin ? "Incharges, routing rules and escalation."
                         : "The categories you are incharge of — manage their sub-categories and escalation."}
      actions={canAdmin ? (
        <CategoryDialog team={team.data} allUsers={allUsers.data} incharges={incharges.data} onSaved={cats.reload} trigger={<Button className="rounded-full bg-cobalt-500 hover:bg-cobalt-600 text-white gap-1.5"><Plus className="h-4 w-4" /> New service</Button>} />
      ) : null}>

      {canAdmin && <InchargesPanel team={team.data} allUsers={allUsers.data} incharges={incharges.data} onChanged={incharges.reload} />}

      {!canAdmin && (
        <div className="glass rounded-2xl p-5 mb-6">
          <div className="overline mb-1">You are incharge of</div>
          {myAuthNames.length ? (
            <>
              <div className="flex flex-wrap gap-2 mt-2">
                {myAuthNames.map((n) => (
                  <span key={n} className="text-xs font-medium px-3 py-1.5 rounded-full bg-cobalt-500/10 text-cobalt-500">{n}</span>
                ))}
              </div>
              <p className="text-xs text-muted-foreground mt-3">
                {visible.length === 0
                  ? "No categories have been assigned to you yet. Ask your central admin to set you as the incharge on a category."
                  : `You manage ${visible.length} categor${visible.length === 1 ? "y" : "ies"} below. Use the tree icon on a card to add sub-categories and set their escalation.`}
              </p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground mt-1">
              You are not currently set as an incharge. Ask your central admin to add you under
              Categories &rarr; Category incharges, and to set you as the incharge on a category.
            </p>
          )}
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="categories-grid">
        {visible.map((c, i) => (
          <motion.div
            key={c.id}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="glass rounded-2xl p-5 relative"
            data-testid={`cat-card-${c.id}`}
          >
            <div className="flex items-start justify-between">
              <div className="h-10 w-10 rounded-xl grid place-items-center" style={{ background: `${colorFor(c.id)}18`, color: colorFor(c.id) }}>
                <span className="font-display font-black text-lg">{(c.name?.[0] || "•").toUpperCase()}</span>
              </div>
              <CategoryDeleteButton cat={c} onDeleted={cats.reload} />
              <SubCategoryDialog cat={c} team={team.data} onChanged={cats.reload} trigger={
                <Button size="icon" variant="ghost" className="h-7 w-7" title="Sub-categories & escalation">
                  <ListTree className="h-3.5 w-3.5" />
                </Button>
              } />
              <CategoryDialog cat={c} team={team.data} allUsers={allUsers.data} incharges={incharges.data} onSaved={cats.reload} trigger={
                <Button size="icon" variant="ghost" className="h-7 w-7" title="Edit service"><Pencil className="h-3.5 w-3.5" /></Button>
              } />
            </div>
            <div className="mt-3 font-display text-xl font-extrabold tracking-tighter">{c.name}</div>
            {(c.sub_categories || []).filter((x) => x.is_active !== false).length > 0 && (
              <div className="text-[11px] text-muted-foreground mt-0.5">
                {(c.sub_categories || []).filter((x) => x.is_active !== false).length} sub-categories
              </div>
            )}
            <div className="text-xs text-muted-foreground mt-1">{c.description || "Auto-routes on creation."}</div>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-cobalt-500/10 text-cobalt-600 dark:text-cobalt-300">
                {c.authority?.name
                  ? `INCHARGE · ${c.authority.name}${c.authority.department ? " (" + c.authority.department + ")" : ""}`
                  : "NO INCHARGE"}
              </span>
              {(c.sub_categories || []).filter((x) => x.is_active !== false).length > 0 && (
                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10">
                  {(c.sub_categories || []).filter((x) => x.is_active !== false).length} SUB
                </span>
              )}
              {c.is_confidential && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-600 dark:text-amber-300">Confidential</span>}
              {c.allow_anonymous && <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full bg-black/5 dark:bg-white/10">Anonymous ok</span>}
            </div>
            <div className="mt-4 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">SLA</span>
              <span className="text-mono">{c.sla_hours || 24}h</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Default assignee</span>
              <span className="text-mono">{c.assignee?.name?.split(" ")[0] || "—"}</span>
            </div>
            <div className="mt-2 flex items-center justify-between text-xs">
              <span className="text-muted-foreground">Active</span>
              <span className={`text-mono ${c.is_active ? "text-emerald-600" : "text-zinc-500"}`}>{c.is_active ? "yes" : "no"}</span>
            </div>
          </motion.div>
        ))}
        {cats.data?.length === 0 && <div className="text-sm text-muted-foreground col-span-full glass rounded-2xl p-6">No categories configured yet.</div>}
      </div>
    </Shell>
  );
}

/* ============================================================
 * Settings — editable policy + registration code
 * ============================================================ */
function SettingsPage() {
  const inst = useFetch(() => api.institute().then((r) => r?.institute || {}), []);
  const i = inst.data || {};
  const [approval, setApproval] = useState(null);
  const busyRef = approval !== null;
  const effectiveApproval = approval !== null ? approval : !!i.require_approval;

  const toggleApproval = async (v) => {
    setApproval(v);
    try {
      await api.updateInstitute({ require_approval: v });
      toast.success("Updated");
      inst.reload();
    } catch (e) { toast.error(e.message || "Failed"); setApproval(null); }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(i.code || "");
    toast.success("Registration code copied");
  };

  return (
    <Shell role="admin" title="Settings" subtitle="Institute preferences.">
      <div className="glass rounded-2xl p-5 max-w-2xl space-y-5">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <Field label="Institute" value={i.name} />
          <Field label="Plan" value={i.plan} />
          <Field label="Email mode" value={i.email_mode} />
          <Field label="Allowed domains" value={i.allowed_domains || "any"} mono />
          <Field label="Passwordless" value={i.passwordless ? "on" : "off"} />
          <Field label="Pilot ends" value={i.pilot_ends_at ? hoursAgo(i.pilot_ends_at) : "—"} />
        </div>

        <div className="rounded-xl border p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Registration code</div>
            <div className="text-xs text-muted-foreground">Share with staff & students to register.</div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-mono text-sm px-2 py-1 rounded bg-black/5 dark:bg-white/5">{i.code}</span>
            <Button size="icon" variant="outline" className="h-8 w-8" onClick={copyCode}><Copy className="h-3.5 w-3.5" /></Button>
          </div>
        </div>

        <div className="rounded-xl border p-4 flex items-center justify-between">
          <div>
            <div className="text-sm font-medium">Require approval for new registrations</div>
            <div className="text-xs text-muted-foreground">Pending users land in the Users & Team queue until you approve them.</div>
          </div>
          <Switch checked={effectiveApproval} onCheckedChange={toggleApproval} disabled={busyRef} data-testid="settings-approval-toggle" />
        </div>

        <p className="text-xs text-muted-foreground pt-2 border-t flex items-center gap-1.5">
          <Mail className="h-3.5 w-3.5" /> Email sending mode and branding are managed by Arventiq Labs in the Superadmin panel.
        </p>

        <div className="pt-4 border-t">
          <WhatsAppConfig />
        </div>
      </div>
    </Shell>
  );
}

const Field = ({ label, value, mono }) => (
  <div>
    <div className="overline">{label}</div>
    <div className={`mt-1 ${mono ? "text-mono text-xs" : ""}`}>{value || "—"}</div>
  </div>
);

// Some Admin Console pages (Users & Team, Categories, Settings) manage
// institute-wide policy, other people's passwords, and integration secrets.
// Technicians work tickets — they should not have access to these, so we
// gate them here as well, not just hide them from the sidebar.
function AdminOnly({ children }) {
  const { user } = useAuth();
  if (!isAdmin(user)) return <Navigate to="/admin" replace />;
  return children;
}

// Admins AND category heads may manage categories. Technicians/requesters may not.
function ManagerOnly({ children }) {
  const { user } = useAuth();
  const allowed = isAdmin(user) || user?.role === "authority";
  if (!allowed) return <Navigate to="/admin" replace />;
  return children;
}

function ReportsPage() {
  return (
    <Shell role="admin" title="Reports" subtitle="Download branded performance reports.">
      <ReportsView />
    </Shell>
  );
}

export default function Admin() {
  return (
    <Routes>
      <Route index element={<Overview />} />
      <Route path="reports" element={<ReportsPage />} />
      <Route path="kanban" element={<KanbanPage />} />
      <Route path="queue" element={<QueuePage />} />
      <Route path="my-requests" element={<MyRequestsPage />} />
      <Route path="help" element={<Help />} />
      <Route path="team" element={<AdminOnly><TeamPage /></AdminOnly>} />
      <Route path="categories" element={<ManagerOnly><CategoriesPage /></ManagerOnly>} />
      <Route path="settings" element={<AdminOnly><SettingsPage /></AdminOnly>} />
      <Route path="knowledge" element={<AdminOnly><Knowledge /></AdminOnly>} />
      <Route path="users/bulk-import" element={<AdminOnly><BulkImportUsers /></AdminOnly>} />
      {/* unknown sub-path -> go home instead of rendering nothing */}
      <Route path="*" element={<Navigate to="/admin" replace />} />
    </Routes>
  );
}
